import { normalizeSceneStatus } from '../../game/sceneLoader';
import {
  DEFAULT_CHARACTER_SCALE_MULTIPLIER,
  DEFAULT_SCENE_CHARACTER_SCALE,
  deriveLegacyCharacterScaleModel,
  normalizeCharacterScaleMultiplier,
  normalizeCharacterScale,
  type CharacterScale,
} from '../../game/avatarScale';
import {
  DEFAULT_WORLD_LAYOUT,
  clampTilePoint,
  normalizeCameraZoom,
  normalizeWorldLayout,
  type WorldLayout,
} from '../../game/worldLayout';
import { normalizeWorldAnnotations } from '../../lib/worldAnnotations';
import { canonicalizeProviderModel, normalizeFallbackProviders } from '../../lib/llmConfig';
import {
  DEFAULT_LLM_MODEL,
  DEFAULT_NPC_MAX_TOKENS,
  DEFAULT_NPC_TEMPERATURE,
} from '../../npc/config';
import {
  normalizeAnnotationRuntimeStateById,
  stripRuntimeStateFromAnnotations,
} from '../../lib/worldAnnotationRuntime';
import { normalizeAnnotationTriggerRuntimeStateById } from '../../lib/worldAnnotationTriggers';
import { normalizeAvatarLayerSelection } from '../../lib/avatarLayerSelection';
import { normalizeInlineSpriteColors } from '../../lib/spriteColors';
import type { SceneDocument, SceneNpcPlacement } from '../../types/scene';
import {
  resolveSceneAssetUrlAsync,
  toResolvedSceneAnimatedGifPlacementAsync,
} from './sceneAssetUrls';
import { parseMovementConfigCandidate } from '../../lib/npcMovement';

const DEFAULT_CHARACTER_SCALE: CharacterScale = DEFAULT_SCENE_CHARACTER_SCALE;
type InlineNpcConfigRecord = NonNullable<SceneNpcPlacement['inlineConfig']>;

const toNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toTimestampDate = (value: unknown): Date | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as { toDate?: () => Date };
  if (typeof candidate.toDate !== 'function') {
    return null;
  }

  try {
    return candidate.toDate();
  } catch {
    return null;
  }
};

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map((entry) => String(entry).trim())
        .filter((entry) => entry.length > 0)
        .slice(0, 10)
    : [];

const toTilePoint = (
  value: unknown,
  fallback: { x: number; y: number },
): { x: number; y: number } => {
  if (!value || typeof value !== 'object') {
    return fallback;
  }
  const entry = value as Record<string, unknown>;
  return {
    x: Math.max(0, Math.floor(toNumber(entry.x, fallback.x))),
    y: Math.max(0, Math.floor(toNumber(entry.y, fallback.y))),
  };
};

const toInlineNpcConfig = (value: unknown): SceneNpcPlacement['inlineConfig'] | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  const name = typeof entry.name === 'string' ? entry.name.trim() : '';
  const title = typeof entry.title === 'string' ? entry.title.trim() : '';
  if (!name) {
    return undefined;
  }
  const llmProvider =
    entry.llmProvider === 'openrouter' || entry.llmProvider === 'anthropic'
      ? entry.llmProvider
      : 'gemini';
  const spriteId =
    typeof entry.spriteId === 'string' && entry.spriteId.trim().length > 0
      ? entry.spriteId.trim()
      : undefined;
  const avatarLayers = normalizeAvatarLayerSelection(entry.avatarLayers);
  const spriteColors = normalizeInlineSpriteColors(entry.spriteColors, {
    avatarLayers,
    spriteId,
  });
  return {
    name,
    title,
    expertise: typeof entry.expertise === 'string' ? entry.expertise.trim() : '',
    backstory: typeof entry.backstory === 'string' ? entry.backstory.trim() : '',
    personality: typeof entry.personality === 'string' ? entry.personality.trim() : '',
    greeting: typeof entry.greeting === 'string' ? entry.greeting.trim() : '',
    ...(spriteId ? { spriteId } : {}),
    ...(avatarLayers ? { avatarLayers } : {}),
    ...(spriteColors ? { spriteColors } : {}),
    avatarMode: entry.avatarMode as InlineNpcConfigRecord['avatarMode'],
    customAvatarId:
      typeof entry.customAvatarId === 'string'
        ? entry.customAvatarId.trim() || null
        : entry.customAvatarId === null
          ? null
          : undefined,
    personalitySpec: (entry.personalitySpec as InlineNpcConfigRecord['personalitySpec']) ?? null,
    llmProvider,
    llmModel: canonicalizeProviderModel(
      llmProvider,
      String(entry.llmModel ?? '').trim() || DEFAULT_LLM_MODEL,
    ),
    fallbackProviders: normalizeFallbackProviders(entry.fallbackProviders),
    temperature: toNumber(entry.temperature, DEFAULT_NPC_TEMPERATURE),
    maxTokens: Math.max(32, Math.floor(toNumber(entry.maxTokens, DEFAULT_NPC_MAX_TOKENS))),
    agentConfig: (entry.agentConfig as InlineNpcConfigRecord['agentConfig']) ?? null,
  };
};

export const toSceneNpcPlacement = (
  value: unknown,
  index: number,
  layout: WorldLayout = DEFAULT_WORLD_LAYOUT,
): SceneNpcPlacement | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const entry = value as Record<string, unknown>;
  const sourceRaw = String(entry.source ?? '').trim();
  const source =
    sourceRaw === 'system' || sourceRaw === 'user' || sourceRaw === 'inline' ? sourceRaw : null;
  if (!source) {
    return null;
  }

  const normalizedLayout = normalizeWorldLayout(layout);
  const waypoints = Array.isArray(entry.waypointPath)
    ? entry.waypointPath
        .map((waypoint) => clampTilePoint(toTilePoint(waypoint, { x: 0, y: 0 }), normalizedLayout))
        .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
    : [];
  const spawnTile = clampTilePoint(
    {
      x: Math.max(0, Math.floor(toNumber(entry.spawnX, 2))),
      y: Math.max(0, Math.floor(toNumber(entry.spawnY, 2))),
    },
    normalizedLayout,
  );

  return {
    placementId:
      String(entry.placementId ?? `placement-${index + 1}`).trim() || `placement-${index + 1}`,
    source,
    sourceNpcId: entry.sourceNpcId ? String(entry.sourceNpcId).trim() : undefined,
    inlineConfig: toInlineNpcConfig(entry.inlineConfig),
    spawnX: spawnTile.x,
    spawnY: spawnTile.y,
    zone: String(entry.zone ?? 'default').trim() || 'default',
    movementConfig: parseMovementConfigCandidate(entry.movementConfig, waypoints, normalizedLayout),
    waypointPath: waypoints,
  };
};

const toCharacterScaleModel = (
  value: unknown,
  multiplierValue: unknown,
): Pick<SceneDocument, 'characterScale' | 'characterScaleMultiplier'> => {
  const hasExplicitMultiplier = Number.isFinite(Number(multiplierValue));
  const normalizedMultiplier = hasExplicitMultiplier
    ? normalizeCharacterScaleMultiplier(Number(multiplierValue))
    : DEFAULT_CHARACTER_SCALE_MULTIPLIER;

  if (!value || typeof value !== 'object') {
    return {
      characterScale: DEFAULT_CHARACTER_SCALE,
      characterScaleMultiplier: normalizedMultiplier,
    };
  }

  const entry = value as Record<string, unknown>;
  const normalizedCharacterScale = normalizeCharacterScale({
    npc: toNumber(entry.npc, DEFAULT_CHARACTER_SCALE.npc),
    playerFallback: toNumber(entry.playerFallback, DEFAULT_CHARACTER_SCALE.playerFallback),
    playerAvatar: toNumber(entry.playerAvatar, DEFAULT_CHARACTER_SCALE.playerAvatar),
    remoteNpc: toNumber(entry.remoteNpc, DEFAULT_CHARACTER_SCALE.remoteNpc),
    remoteAvatar: toNumber(entry.remoteAvatar, DEFAULT_CHARACTER_SCALE.remoteAvatar),
  });

  if (hasExplicitMultiplier) {
    return {
      characterScale: normalizedCharacterScale,
      characterScaleMultiplier: normalizedMultiplier,
    };
  }

  const legacyModel = deriveLegacyCharacterScaleModel(normalizedCharacterScale);
  return {
    characterScale: legacyModel.baseScale,
    characterScaleMultiplier: legacyModel.characterScaleMultiplier,
  };
};

const toAmbientProfile = (value: unknown): Record<string, { frequency: number; gain: number }> => {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const raw = value as Record<string, unknown>;
  const profile: Record<string, { frequency: number; gain: number }> = {};
  Object.entries(raw).forEach(([zoneId, zoneValue]) => {
    if (!zoneValue || typeof zoneValue !== 'object') {
      return;
    }
    const entry = zoneValue as Record<string, unknown>;
    profile[zoneId] = {
      frequency: toNumber(entry.frequency, 120),
      gain: toNumber(entry.gain, 0.003),
    };
  });
  return profile;
};

const toWorldLayout = (value: unknown): SceneDocument['worldLayout'] => {
  return normalizeWorldLayout(value);
};

export const toSceneDocument = async (
  id: string,
  data: Record<string, unknown>,
  layoutOverride?: WorldLayout,
): Promise<SceneDocument> => {
  const statusFallback = data.publishedAt ? 'published' : 'draft';
  const worldLayout = layoutOverride
    ? normalizeWorldLayout(layoutOverride)
    : toWorldLayout(data.worldLayout);
  const [backgroundUrl, foregroundUrl, traversalMaskUrl, thumbnailUrl, animatedGifs] =
    await Promise.all([
      resolveSceneAssetUrlAsync(data.backgroundUrl),
      resolveSceneAssetUrlAsync(data.foregroundUrl),
      resolveSceneAssetUrlAsync(data.traversalMaskUrl),
      resolveSceneAssetUrlAsync(data.thumbnailUrl),
      Array.isArray(data.animatedGifs)
        ? Promise.all(
            data.animatedGifs.map((entry, index) =>
              toResolvedSceneAnimatedGifPlacementAsync(entry, index),
            ),
          )
        : Promise.resolve([]),
    ]);
  const characterScaleModel = toCharacterScaleModel(
    data.characterScale,
    data.characterScaleMultiplier,
  );

  return {
    id,
    name: String(data.name ?? 'Untitled Scene').trim() || 'Untitled Scene',
    description: String(data.description ?? '').trim(),
    tags: toStringArray(data.tags),
    ownerId: String(data.ownerId ?? 'system').trim() || 'system',
    ownerDisplayName: String(data.ownerDisplayName ?? 'Unknown').trim() || 'Unknown',
    status: normalizeSceneStatus(data.status, statusFallback),
    levelDesignId: String(data.levelDesignId ?? '').trim(),
    backgroundUrl,
    foregroundUrl,
    traversalMaskUrl,
    thumbnailUrl,
    animatedGifs: animatedGifs.filter(
      (entry): entry is NonNullable<SceneDocument['animatedGifs']>[number] => entry !== null,
    ),
    worldLayout,
    defaultCameraZoom: normalizeCameraZoom(data.defaultCameraZoom),
    playerSpawnTile: clampTilePoint(
      toTilePoint(data.playerSpawnTile, { x: 32, y: 32 }),
      worldLayout,
    ),
    defaultZone: String(data.defaultZone ?? 'default').trim() || 'default',
    zones: Array.isArray(data.zones) ? (data.zones as SceneDocument['zones']) : [],
    signs: Array.isArray(data.signs) ? (data.signs as SceneDocument['signs']) : [],
    doors: Array.isArray(data.doors) ? (data.doors as SceneDocument['doors']) : [],
    annotations: stripRuntimeStateFromAnnotations(
      normalizeWorldAnnotations(
        Array.isArray(data.annotations) ? (data.annotations as SceneDocument['annotations']) : [],
        worldLayout,
      ),
    ),
    annotationRuntimeStateById: normalizeAnnotationRuntimeStateById(
      data.annotationRuntimeStateById,
    ),
    annotationTriggerRuntimeStateById: normalizeAnnotationTriggerRuntimeStateById(
      data.annotationTriggerRuntimeStateById,
    ),
    npcPlacements: Array.isArray(data.npcPlacements)
      ? data.npcPlacements
          .map((entry, index) => toSceneNpcPlacement(entry, index, worldLayout))
          .filter((entry): entry is SceneNpcPlacement => entry !== null)
      : [],
    characterScale: characterScaleModel.characterScale,
    characterScaleMultiplier: characterScaleModel.characterScaleMultiplier,
    ambientZoneProfile: toAmbientProfile(data.ambientZoneProfile),
    visitCount: Math.max(0, Math.floor(toNumber(data.visitCount, 0))),
    createdAt: toTimestampDate(data.createdAt),
    updatedAt: toTimestampDate(data.updatedAt),
    publishedAt: toTimestampDate(data.publishedAt),
  };
};
